const ArgumentType = require('../../extension-support/argument-type');
const BlockType = require('../../extension-support/block-type');
const formatMessage = require('format-message');
const SensorPeripheral = require('../common/sensor-peripheral');
const { default: bleUtils } = require('../../util/ble');
const { BLE_CONFIG } = require('../../util/bleCharac');

/**
 * 蜂鸣器模块
 * 
 * @readonly
 */
const DEVICE_OPT = {
    type: 'sensor'
};

/**
 * Manage communication with a YM-BEEP peripheral over a Scratch Link client socket.
 */
class YMBEEPPeripheral extends SensorPeripheral {
    constructor(runtime, deviceId, originalDeviceId) {
        super(runtime, deviceId, originalDeviceId, null, BLE_CONFIG, DEVICE_OPT);
    }
}

class ScratchYMBEEPDevice {
    get DEVICE_ID() {
        return 'YMBEEP';
    }

    constructor(runtime, originalDeviceId) {
        this.runtime = runtime;
        this._peripheral = new YMBEEPPeripheral(this.runtime, this.DEVICE_ID, originalDeviceId);
        this.bleUtils = new bleUtils();
    }

    getInfo() {
        return [{
            id: 'YMBEEP',
            name: formatMessage({
                id: 'YMBEEP.categoryName',
                default: 'YM BEEP',
                description: 'Label for the YM BEEP extension category'
            }),
            color1: "#FF5722",
            color2: "#FF7043",
            color3: "#F4511E",
            blocks: [
                {
                    opcode: 'connect',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMBEEP.ble.connect',
                        default: 'connect to YM-BEEP-[DEVICE_NAME]',
                        description: 'Connect to YM BEEP'
                    }),
                    arguments: {
                        DEVICE_NAME: {
                            type: ArgumentType.STRING,
                            defaultValue: ' ',
                        }
                    }
                },
                {
                    opcode: 'disConnect',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMBEEP.ble.disconnect',
                        default: 'disconnect from YM BEEP',
                        description: 'Disconnect from YM BEEP'
                    })
                },
                {
                    opcode: 'beepOn',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMBEEP.beepOn',
                        default: 'turn beep [SWITCH]',
                        description: 'Turn the beep on or off'
                    }),
                    arguments: {
                        SWITCH: {
                            type: ArgumentType.NUMBER,
                            menu: 'beepSwitch'
                        }
                    }
                },
                {
                    opcode: 'setBeepFreq',
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: 'YMBEEP.setBeepFreq',
                        default: 'set beep frequency [FREQ] Hz',
                        description: 'Set beep frequency'
                    }),
                    arguments: {
                        FREQ: {
                            type: ArgumentType.NUMBER,
                            defaultValue: 1000
                        }
                    }
                }
            ],
            menus: {
                beepSwitch: {
                   
                    items: [
                        { text: formatMessage({ id: 'YMBEEP.beepOn.1', default: 'on' }), value: '1' },
                        { text: formatMessage({ id: 'YMBEEP.beepOff.0', default: 'off' }), value: '0' }
                    ]
                }
            }
        }];
    }

    async connect(args) {
        const deviceName = 'YM-BEEP-' + (args.DEVICE_NAME?.trim() || "");
        const connected = await this.isConnected();
        if (connected) {
            console.log('Already connected to device');
            return;
        }
        console.log(`Connecting to YM BEEP device: ${deviceName}`);
        try {
            await this.bleUtils.connect(deviceName, BLE_CONFIG.SERVICE_UUID);
            console.log(`Connected to device: ${deviceName}`);
        } catch (error) {
            console.error(`Failed to connect to ${deviceName}:`, error);
        }
    }

    async isConnected() {
        return this.bleUtils.device !== null && this.bleUtils.device.gatt.connected;
    }

    async disConnect() {
        if (this.bleUtils.device) {
            try {
                await this.bleUtils.disConnect(this.bleUtils.device);
                console.log('Disconnected from device');
            } catch (error) {
                console.error('Failed to disconnect:', error);
            }
        }
    }

    async beepOn(args) {
        const state = args.SWITCH === 'on' ? 1 : 0;
        if (!await this.isConnected()) {
            console.warn('Not connected to YM BEEP device');
            return;
        }
        try {
            const characteristic = await this.bleUtils.getCharacteristic(this.bleUtils.service, BLE_CONFIG.beepSwitch);
            const data = new Uint8Array([state]);
            await this.bleUtils.writeCharacteristic(characteristic, data);
            console.log(`Beep turned ${args.SWITCH}`);
        } catch (error) {
            console.error('Failed to set beep state:', error);
        }
    }

    async setBeepFreq(args) {
        const freq = Number(args.FREQ) || 0;
        if (!await this.isConnected()) {
            console.warn('Not connected to YM BEEP device');
            return;
        }
        // Frequency is sent as 2 bytes (little endian)
        try {
            const characteristic = await this.bleUtils.getCharacteristic(this.bleUtils.service, BLE_CONFIG.beepFreq);
            const freqBytes = new Uint8Array([freq & 0xFF, (freq >> 8) & 0xFF]);
            await this.bleUtils.writeCharacteristic(characteristic, freqBytes);
            console.log(`Beep frequency set to ${freq} Hz`);
        } catch (error) {
            console.error('Failed to set beep frequency:', error);
        }
    }
}

module.exports = ScratchYMBEEPDevice;
